import { graphql, PageProps } from 'gatsby';
import React from 'react';

import Hero from '../components/blocks/hero';
import MultiBlogrollAlt from '../components/blocks/MultiBlogrollAlt';
import ResourceDetail from '../components/blocks/ResourceDetail';
import Subnav from '../components/blocks/Subnav';
import Layout from '../components/layout';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import { useResourcesSubnav } from '../hooks/use-resources-subnav';
import { PageTemplate } from '../utils/match-page-template';

export type ResourcesPageData = Queries.ResourcesTemplateQuery;

export interface ArticleResult {
    title: string,
    url: string,
    featured_image?: {
        readonly filename: string | null;
        readonly title: string | null;
        readonly url: string | null;
        readonly contentStackAsset: Record<string, unknown> | null;
    }
    content: string
}

export interface IndexPosts {
    heading: string,
    url: string,
    articles: ArticleResult[]
}

export interface ResourcesPageContext {
    slug: string,
    title: string,
    showSubNav: boolean,
    articles: {
        isIndex: boolean,
        subblog: IndexPosts[],
        primaryArticle?: ArticleResult
    },
    templateName: PageTemplate.ResourceIndex,
}

export interface TransformedArticle {
    title: string,
    copy: string,
    image: ContentStackImage,
    read_more: string,
}

const Resources: React.FC<PageProps<ResourcesPageData, ResourcesPageContext>> = ({ data, pageContext }) => {
    // Resources Subnav
    const resourcesSubnav = useResourcesSubnav();
    const subnavData = { subnav: [resourcesSubnav] };
    const { title: pageTitle } = pageContext;

    // CMS Content Type: Resource Page
    const { hero, seo } = data.cmsResourcesPage ?? { hero: undefined, seo: undefined };
    // Blogroll data / page context from node
    const { articles, showSubNav } = pageContext;
    const { isIndex, primaryArticle, subblog } = articles;

    const articleToResourceDetail = (article: ArticleResult): TransformedArticle => {
        const { title: articleTitle, content, featured_image, url } = article;

        return {
            title: articleTitle,
            copy: content,
            image: featured_image as ContentStackImage,
            read_more: url,
        };
    };

    return (
        <CtaProvider>
            <Layout>
                <Seo
                    title={seo?.meta_title || pageTitle}
                    description={seo?.meta_description}
                    imageUrl={seo?.meta_image?.url || hero?.image?.url}
                />
                {!!hero && <Hero {...hero} />}
                {!!showSubNav && <Subnav data={subnavData} />}
                {!!primaryArticle && (
                    <ResourceDetail data={articleToResourceDetail(primaryArticle)} />
                )}
                <MultiBlogrollAlt data={{ subblog, isIndex }} />
            </Layout>
        </CtaProvider>
    );
};

export const query = graphql`
  query ResourcesTemplate ($slug: String!) {
    cmsResourcesPage(url: { eq: $slug }) {
      title
      url
      seo {
        meta_title
        meta_description
        meta_image {
          url
        }
      }
      hero {
        fullwidth_background
        heading
        subheading
        copy
        button_link {
          href
          title
        }
        image {
          filename
          title
          url
          contentStackAsset(width: 720)
        }
      }
    }
  }
`;

export default Resources;
